import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Plus, Minus, ChevronRight } from "lucide-react";

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold });
    o.observe(el); return () => o.disconnect();
  }, [threshold]);
  return [ref, v];
};

const servicesData = [
  {
    n: "01", title: "Custom Software Development",
    short: "Tailored software solutions built precisely for your business processes",
    body: "Our expert development team creates custom software applications that perfectly align with your business processes. From web applications to desktop software, we build scalable, maintainable solutions using the latest technologies including React, Node.js, Python, and .NET.",
    features: ["Full-stack web development", "Desktop application development", "API design & integration", "Database design & optimization", "Quality assurance & testing", "Post-launch maintenance"],
    img: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "02", title: "Cloud Solutions & Migration",
    short: "Seamless migration and comprehensive cloud infrastructure management",
    body: "Transform your business with our cloud services. We help you migrate to AWS, Azure, and Google Cloud, ensuring minimal downtime and maximum security. Our cloud architects design scalable infrastructures that grow with your business.",
    features: ["Cloud migration strategy", "Multi-cloud & hybrid solutions", "Cloud security & compliance", "DevOps & CI/CD pipelines", "Cost optimization", "24/7 cloud monitoring"],
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "03", title: "Cybersecurity Services",
    short: "Comprehensive security to protect your digital assets end-to-end",
    body: "Protect your business from cyber threats with our advanced security solutions. We provide security assessments, implement robust measures, and offer ongoing monitoring to ensure your data and systems remain secure.",
    features: ["Security audits & assessments", "Network security management", "Endpoint protection", "Data encryption & backup", "Compliance (GDPR, HIPAA)", "Security awareness training"],
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "04", title: "Mobile App Development",
    short: "Native and cross-platform mobile apps for iOS and Android",
    body: "Create engaging mobile experiences with our development services. We build native iOS and Android apps as well as cross-platform solutions using React Native and Flutter — designed for high performance and intuitive UX.",
    features: ["Native iOS & Android", "Cross-platform (React Native)", "UI/UX design & prototyping", "App store deployment", "Mobile app QA testing", "Post-launch updates"],
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "05", title: "Data Analytics & BI",
    short: "Transform raw data into clear, actionable business intelligence",
    body: "Unlock the power of your data with our analytics and business intelligence solutions. We help you collect, analyze, and visualize data to make informed decisions using Tableau, Power BI, and custom Python pipelines.",
    features: ["Data warehouse design", "Interactive dashboard dev", "Predictive analytics & ML", "Real-time data processing", "Data visualization", "BI consulting"],
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "06", title: "IT Infrastructure Management",
    short: "Complete infrastructure setup, monitoring, and maintenance",
    body: "Ensure your IT infrastructure runs smoothly with our comprehensive management. We handle everything from server setup and network configuration to ongoing monitoring — proactively preventing issues before they impact operations.",
    features: ["Server setup & configuration", "Network design", "Hardware procurement", "System monitoring & alerts", "Backup & disaster recovery", "Performance optimization"],
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=60",
  },
  {
    n: "07", title: "Network Solutions",
    short: "Robust network design, implementation, and optimization",
    body: "Build a reliable and secure network foundation for your business. Our network engineers design and implement scalable solutions ensuring fast, secure, and reliable connectivity for wired and wireless environments.",
    features: ["LAN/WAN network design", "Wireless implementation", "Network security config", "Bandwidth optimization", "Network troubleshooting", "VPN setup & management"],
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT5NSeP7GqE0xbWI8OPPUAYhhj1-Gbg8_jKQw&s",
  },
  {
    n: "08", title: "Technical Support",
    short: "24/7 helpdesk and technical support services",
    body: "Get immediate help when you need it most. Our certified technicians provide round-the-clock support to resolve issues quickly and minimize downtime via phone, email, and remote assistance.",
    features: ["24/7 helpdesk support", "Remote desktop assistance", "On-site technical support", "Hardware & software troubleshoot", "System maintenance & updates", "User training & documentation"],
    img: "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=800&auto=format&fit=crop&q=60",
  },
];

const ServiceRow = ({ s, index, globalIn }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`svc-row ${globalIn ? "vis" : ""} ${open ? "svc-row--open" : ""}`}
      style={{ transitionDelay: `${index * 0.07}s` }}
    >
      {/* Header — always visible */}
      <div className="svc-row-head" onClick={() => setOpen(!open)}>
        <span className="svc-row-n">{s.n}</span>
        <div className="svc-row-meta">
          <h3 className="svc-row-title">{s.title}</h3>
          <p className="svc-row-short">{s.short}</p>
        </div>
        <button className="svc-row-tog" aria-label="toggle">
          {open ? <Minus size={18} /> : <Plus size={18} />}
        </button>
      </div>

      {/* Expanded body */}
      <div className="svc-row-body" style={{ maxHeight: open ? "700px" : "0" }}>
        <div className="svc-row-inner">
          <div className="svc-row-img">
            <img src={s.img} alt={s.title} />
          </div>
          <div className="svc-row-detail">
            <p className="svc-row-desc">{s.body}</p>
            <div className="svc-row-feats">
              <div className="svc-feat-label">Key Capabilities</div>
              <ul>
                {s.features.map((f, i) => (
                  <li key={i}><ChevronRight size={13} />{f}</li>
                ))}
              </ul>
            </div>
            <button className="svc-cta-btn" onClick={() => window.location.href = "/contact"}>
              Request a Quote <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Services() {
  const [listRef, listIn] = useInView(0.05);
  const [whyRef, whyIn] = useInView(0.1);


  const why = [
    { n: "01", t: "Proven Expertise", d: "15+ years delivering complex IT projects across diverse industries with measurable outcomes." },
    { n: "02", t: "End-to-End Ownership", d: "We take full responsibility from scoping to post-launch support — no hand-offs, no gaps." },
    { n: "03", t: "Technology Agnostic", d: "We recommend the best tool for the job, not the one we're most comfortable with." },
  ];

  return (
    <div className="sp">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Instrument+Serif:ital@0;1&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        :root{
          --blue:#2563eb;--blue2:#3b82f6;
          --black:#080808;--dark:#0f0f0f;
          --lgray:#f7f7f7;--white:#fff;
          --txd:#111;--txm:#555;--txl:#999;
          --bd:rgba(0,0,0,.08);--bdd:rgba(11, 10, 10, 0.07);
          --f:'Inter',sans-serif;--serif:'Instrument Serif',serif;
        }
        .sp{font-family:var(--f);background:var(--black);overflow-x:hidden}

        /* ── HERO ── */
        .sp-hero{
          position:relative;min-height:60vh;background:var(--black);
          display:flex;flex-direction:column;align-items:center;justify-content:center;
          text-align:center;padding:10rem 1.5rem 5rem;overflow:hidden;
        }
        .sp-hero-dots{
          position:absolute;inset:0;z-index:0;
          background-image:radial-gradient(circle,rgba(255,255,255,.06) 1px,transparent 1px);
          background-size:32px 32px;
          mask-image:radial-gradient(ellipse 70% 60% at 50% 50%,#000 20%,transparent 100%);
        }
        .sp-hero-glow{
          position:absolute;top:-80px;left:50%;transform:translateX(-50%);
          width:700px;height:350px;border-radius:50%;
          background:radial-gradient(ellipse,rgba(37,99,235,.2),transparent 65%);
          filter:blur(50px);z-index:0;pointer-events:none;
        }
        .sp-hero-line{
          position:absolute;top:0;left:0;right:0;height:1px;
          background:linear-gradient(90deg,transparent,rgba(37,99,235,.5),transparent);
          animation:lineIn .8s ease .1s both;
        }
        @keyframes lineIn{from{opacity:0;transform:scaleX(0)}to{opacity:1;transform:scaleX(1)}}

        .sp-hero-con{position:relative;z-index:1;max-width:780px}
        .sp-badge{
          display:inline-flex;align-items:center;gap:8px;
          background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);
          border-radius:100px;padding:.4rem 1rem;
          font-size:.67rem;font-weight:600;letter-spacing:.12em;text-transform:uppercase;
          color:rgba(255,255,255,.4);margin-bottom:1.75rem;
          opacity:0;animation:fadeUp .7s ease .2s both;
        }
        .sp-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--blue2);animation:blink 2s infinite}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}

        .sp-title{
          font-size:clamp(2.4rem,5vw,4.5rem);font-weight:900;
          line-height:1.02;letter-spacing:-.045em;color:#fff;margin-bottom:1.2rem;
        }
        .sp-title .w{
          display:inline-block;opacity:0;transform:translateY(100%);
          animation:wordUp .8s cubic-bezier(.16,1,.3,1) both;margin-right:.25em;
        }
        .sp-title .w.bl{
          background:linear-gradient(135deg,#93c5fd,#3b82f6);
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
        }
        @keyframes wordUp{to{opacity:1;transform:translateY(0)}}
        .sp-sub{
          font-size:.95rem;font-weight:400;line-height:1.75;
          color:rgba(255,255,255,.38);max-width:480px;margin:0 auto;
          opacity:0;animation:fadeUp .7s ease .8s both;
        }

        /* ── SERVICE LIST (black) ── */
        .sp-list-sec{
          background:var(--black);
          border-top:1px solid var(--bdd);
        }
        .sp-list-wrap{max-width:1100px;margin:0 auto;padding:0 1.5rem}

        .svc-row{
          border-bottom:1px solid rgba(255,255,255,.06);
          opacity:0;transform:translateY(20px);
          transition:opacity .6s ease,transform .6s cubic-bezier(.16,1,.3,1);
        }
        .svc-row.vis{opacity:1;transform:translateY(0)}
        .svc-row--open{border-color:rgba(37,99,235,.2)}

        .svc-row-head{
          display:grid;grid-template-columns:80px 1fr 48px;
          gap:2rem;align-items:center;
          padding:2.5rem 0;cursor:pointer;
          transition:opacity .2s ease;
        }
        .svc-row-head:hover{opacity:.85}

        .svc-row-n{
          font-size:clamp(3rem,5vw,4.5rem);font-weight:900;
          color:rgba(255,255,255,.07);letter-spacing:-.06em;line-height:1;
          transition:color .3s ease;
          user-select:none;
        }
        .svc-row--open .svc-row-n{color:rgba(255,255,255,.7)}

        .svc-row-title{
          font-size:clamp(1rem,2vw,1.45rem);font-weight:700;
          color:#fff;letter-spacing:-.025em;margin-bottom:.3rem;
        }
        .svc-row-short{font-size:.83rem;color:rgba(255,255,255,.35);line-height:1.5;font-weight:400}

        .svc-row-tog{
          width:42px;height:42px;border-radius:50%;
          background:rgba(255,255,255,.05);border:1px solid var(--bdd);
          color:rgba(255,255,255,.4);cursor:pointer;
          display:flex;align-items:center;justify-content:center;
          transition:all .25s ease;flex-shrink:0;
        }
        .svc-row--open .svc-row-tog{
          background:rgba(37,99,235,.15);border-color:rgba(37,99,235,.4);color:var(--blue2);
        }
        .svc-row-tog:hover{background:rgba(255,255,255,.1)}

        /* Expanded */
        .svc-row-body{
          overflow:hidden;
          transition:max-height .55s cubic-bezier(.4,0,.2,1);
        }
        .svc-row-inner{
          display:grid;grid-template-columns:1fr 1.4fr;
          gap:3rem;padding:0 0 3rem;
          border-top:1px solid rgba(255,255,255,.06);
          padding-top:2rem;
          align-items:start;
        }
        .svc-row-img{border-radius:14px;overflow:hidden;border:1px solid var(--bdd);aspect-ratio:16/9}
        .svc-row-img img{width:100%;height:100%;object-fit:cover;filter:brightness(.7) saturate(.8);transition:all .4s ease}
        .svc-row--open .svc-row-img img{filter:brightness(.9) saturate(1)}

        .svc-row-desc{font-size:.88rem;line-height:1.8;color:rgba(255,255,255,.45);margin-bottom:1.5rem;font-weight:400}
        .svc-feat-label{font-size:.65rem;font-weight:800;letter-spacing:.15em;text-transform:uppercase;color:var(--blue2);margin-bottom:.75rem}
        .svc-row-feats ul{list-style:none;padding:0;margin:0 0 1.75rem;display:grid;grid-template-columns:1fr 1fr;gap:.5rem}
        .svc-row-feats li{display:flex;align-items:center;gap:.4rem;font-size:.8rem;color:rgba(255,255,255,.45)}
        .svc-row-feats li svg{color:var(--blue2);opacity:.7;flex-shrink:0}

        .svc-cta-btn{
          display:inline-flex;align-items:center;gap:.45rem;
          background:var(--blue);border:none;border-radius:8px;color:#fff;
          cursor:pointer;font-family:var(--f);font-size:.8rem;font-weight:700;
          letter-spacing:.05em;text-transform:uppercase;padding:.7rem 1.35rem;
          transition:all .25s ease;
        }
        .svc-cta-btn:hover{background:#1d4ed8;transform:translateY(-2px);box-shadow:0 8px 24px rgba(37,99,235,.45)}

        /* ── WHY US (light gray) ── */
        .sp-why{background:var(--lgray);padding:8rem 1.5rem;border-top:1px solid rgba(0,0,0,.06)}
        .sp-why .wrap{max-width:1100px;margin:0 auto}
        .sec-lbl{display:block;font-size:.67rem;font-weight:800;letter-spacing:.18em;text-transform:uppercase;color:var(--blue);margin-bottom:.85rem}
        .sec-h{font-size:clamp(1.8rem,3vw,2.6rem);font-weight:900;letter-spacing:-.04em;line-height:1.08;color:var(--txd);margin-bottom:4rem}
        .sec-h em{font-style:italic;font-family:var(--serif)}

        .why-rows{display:flex;flex-direction:column}
        .why-row{
          display:grid;grid-template-columns:100px 1fr;gap:3rem;align-items:start;
          padding:3.5rem 0;border-bottom:1px solid var(--txm);
          opacity:0;transform:translateY(20px);
          transition:opacity .6s ease,transform .6s cubic-bezier(.16,1,.3,1);
        }
        .why-row:last-child{border-bottom:none}
        .why-row.vis{opacity:1;transform:translateY(0)}
        /* WHY US NUMBER IN DARK BLUE */
.why-n {
  font-size: 5rem;
  font-weight: 900;
  color:rgb(83, 83, 84); /* dark blue */
  letter-spacing: -0.06em;
  line-height: 0.9;
  user-select: none;
}

/* WHY US TEXT */
.why-t {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111; /* dark black for heading text */
  letter-spacing: -0.025em;
  margin-bottom: 0.6rem;
}

.why-d {
  font-size: 0.9rem;
  line-height: 1.75;
  color:rgb(53, 53, 54); /* dark blue for description text */
  max-width: 540px;
  font-weight: 500; 
}

        /* ── CTA ── */
        .sp-cta{
          background:var(--black);padding:8rem 1.5rem;
          text-align:center;border-top:1px solid var(--bdd);
        }
        .sp-cta h2{font-size:clamp(2rem,4vw,3.5rem);font-weight:900;letter-spacing:-.045em;color:#fff;line-height:1.08;margin-bottom:1.1rem}
        .sp-cta h2 em{font-style:italic;font-family:var(--serif);color:rgba(255,255,255,.45)}
        .sp-cta p{font-size:.92rem;color:rgba(17, 17, 17, 0.38);line-height:1.75;margin-bottom:2.5rem;max-width:440px;margin-left:auto;margin-right:auto}
        .sp-cta-btns{display:flex;align-items:center;justify-content:center;gap:.85rem;flex-wrap:wrap}
        .btn-solid{display:inline-flex;align-items:center;gap:.5rem;background:var(--blue);color:#fff;border:none;border-radius:8px;padding:.82rem 1.65rem;font-family:var(--f);font-size:.85rem;font-weight:700;cursor:pointer;transition:all .25s ease;text-decoration:none;box-shadow:0 4px 20px rgba(37,99,235,.3)}
        .btn-solid:hover{background:#1d4ed8;transform:translateY(-3px);box-shadow:0 12px 32px rgba(37,99,235,.5)}
        .btn-ghost{display:inline-flex;align-items:center;gap:.5rem;background:transparent;border:1px solid rgba(255,255,255,.15);color:rgba(255,255,255,.6);border-radius:8px;padding:.82rem 1.65rem;font-family:var(--f);font-size:.85rem;font-weight:600;cursor:pointer;transition:all .25s ease;text-decoration:none}
        .btn-ghost:hover{background:rgba(255,255,255,.07);color:#fff;transform:translateY(-3px)}

        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}

        @media(max-width:860px){
          .svc-row-head{grid-template-columns:60px 1fr 40px;gap:1.25rem}
          .svc-row-inner{grid-template-columns:1fr;gap:1.5rem}
          .sp-why .why-row{grid-template-columns:60px 1fr;gap:1.5rem}
          .why-n{font-size:3rem}
        }
        @media(max-width:600px){
          .svc-row-head{grid-template-columns:50px 1fr 38px;gap:.85rem;padding:1.75rem 0}
          .svc-row-feats ul{grid-template-columns:1fr}
        }
      `}</style>

      {/* HERO */}
      <div className="sp-hero">
        <div className="sp-hero-dots" /><div className="sp-hero-glow" /><div className="sp-hero-line" />
        <div className="sp-hero-con">
          <div className="sp-badge"><div className="sp-badge-dot" />What We Do</div>
          <h1 className="sp-title">
            {"Our IT".split(" ").map((w, i) => <span key={i} className="w" style={{ animationDelay: `${.3 + i * .12}s` }}>{w}</span>)}
            {" "}
            <span className="w bl" style={{ animationDelay: ".56s" }}>Services</span>
          </h1>
          <p className="sp-sub">Comprehensive solutions designed to accelerate your business growth and digital transformation.</p>
        </div>
      </div>

      {/* SERVICES LIST */}
      <section className="sp-list-sec">
        <div className="sp-list-wrap" ref={listRef}>
          {servicesData.map((s, i) => (
            <ServiceRow key={s.n} s={s} index={i} globalIn={listIn} />
          ))}
        </div>
      </section>

      {/* WHY US — light gray */}
      <section className="sp-why">
        <div className="wrap" ref={whyRef}>
          <span className="sec-lbl">Why WayForward IT</span>
          <h2 className="sec-h">What sets us <em>apart</em></h2>
          <div className="why-rows">
            {why.map((w, i) => (
              <div key={i} className={`why-row ${whyIn ? "vis" : ""}`} style={{ transitionDelay: `${i * .12}s` }}>
                <div className="why-n">{w.n}</div>
                <div>
                  <div className="why-t">{w.t}</div>
                  <div className="why-d">{w.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sp-cta">
        <h2>Ready to Get<br /><em>Started?</em></h2>
        <p>Tell us about your project and we'll connect you with the right team.</p>
        <div className="sp-cta-btns">
          <a href="/contact" className="btn-solid">Contact Us <ArrowRight size={15} /></a>
          <a href="/products" className="btn-ghost">View Products</a>
        </div>
      </section>
    </div>
  );
}
